/**
 * 일자별 예약 현황 — 마스터-디테일
 *
 *  매장 관리(/business) 와 동일한 패턴.
 *  좌측: 선택한 날짜에 예약이 있는 매장 목록 (건수 요약)
 *  우측: 선택한 매장의 예약 목록 (상태 탭 + 직권 취소)
 *
 *  BE 는 날짜 기준 전체 매장 예약을 한 번에 내려주므로, 매장 그룹핑은 클라이언트에서 한다.
 *  호텔링 등 다일 예약은 기간 안에 들어있으면 함께 조회된다.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, CheckCircle2, Download, Search, Store, XCircle } from 'lucide-react';
import {
  dailyReservationsApi,
  type DailyReservationRow,
  type ReservStatus,
  type ReservType,
} from '@/services/api';
import { StatusBadge, EmptyState, type BadgeTone } from '@/components/common';
import { exportToExcel } from '@/utils/excel';

const RESERV_TYPE_LABEL: Record<ReservType, string> = {
  RESERV_TYPE_CLASS: '수업',
  RESERV_TYPE_DAYCARE: '데이케어',
  RESERV_TYPE_HOTEL: '호텔',
  RESERV_TYPE_GROOMING: '미용',
  RESERV_TYPE_TRAINING: '훈련',
};

const STATUS_LABEL: Record<ReservStatus, string> = {
  RESERV_STATUS_TEMP: '임시',
  RESERV_STATUS_REQUESTED: '요청',
  RESERV_STATUS_UPDATED: '수정',
  RESERV_STATUS_CONFIRMED: '확정',
  RESERV_STATUS_CANCELED: '취소',
  RESERV_STATUS_COMPLETED: '완료',
};

const STATUS_TONE: Record<ReservStatus, BadgeTone> = {
  RESERV_STATUS_TEMP: 'neutral',
  RESERV_STATUS_REQUESTED: 'info',
  RESERV_STATUS_UPDATED: 'info',
  RESERV_STATUS_CONFIRMED: 'success',
  RESERV_STATUS_CANCELED: 'danger',
  RESERV_STATUS_COMPLETED: 'neutral',
};

/** 우측 패널 상태 탭 — 요청/수정/임시는 "대기" 하나로 묶는다 */
type StatusTab = 'ALL' | 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELED';

const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'PENDING', label: '대기' },
  { key: 'CONFIRMED', label: '확정' },
  { key: 'COMPLETED', label: '완료' },
  { key: 'CANCELED', label: '취소' },
];

const PENDING_STATUSES: ReadonlySet<ReservStatus> = new Set([
  'RESERV_STATUS_REQUESTED',
  'RESERV_STATUS_UPDATED',
  'RESERV_STATUS_TEMP',
]);

/** 어드민 직권 취소 가능한 상태 (요청/수정/확정) */
/** 운영자 승인 가능 상태 — 예약신청/예약수정 */
const APPROVABLE_STATUSES: ReadonlySet<ReservStatus> = new Set([
  'RESERV_STATUS_REQUESTED',
  'RESERV_STATUS_UPDATED',
]);

const CANCELABLE_STATUSES: ReadonlySet<ReservStatus> = new Set([
  'RESERV_STATUS_REQUESTED',
  'RESERV_STATUS_UPDATED',
  'RESERV_STATUS_CONFIRMED',
]);

function matchesTab(row: DailyReservationRow, tab: StatusTab): boolean {
  switch (tab) {
    case 'ALL':
      return true;
    case 'PENDING':
      return PENDING_STATUSES.has(row.status);
    case 'CONFIRMED':
      return row.status === 'RESERV_STATUS_CONFIRMED';
    case 'COMPLETED':
      return row.status === 'RESERV_STATUS_COMPLETED';
    case 'CANCELED':
      return row.status === 'RESERV_STATUS_CANCELED';
  }
}

function todayStr(): string {
  const d = new Date();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${d.getFullYear()}-${m < 10 ? `0${m}` : m}-${day < 10 ? `0${day}` : day}`;
}

/**
 * 이용권 결제 정보를 표시 ("상품명 (N회)", 상품명 없으면 "-").
 * 코인 결제 예약이면 "-" (이용권 정보이므로 코인 금액은 표시하지 않음).
 */
function formatTicket(row: DailyReservationRow): string {
  if (row.paymentType === 'PAYMENT_TYPE_TICKET') {
    // 호텔은 횟수 개념이 없어 상품명만 표시
    if (row.reservType === 'RESERV_TYPE_HOTEL') {
      return row.ticketName ?? '-';
    }
    const count = `${row.paymentTicket ?? 0}회`;
    return row.ticketName ? `${row.ticketName} (${count})` : '-';
  }
  return '-';
}

/**
 * 예약 시간 표기.
 *
 *  수업/유치원(RESERV_TYPE_CLASS) 예약은 생성 시 START_TIME 을 저장하지 않아 항상 비어 있다.
 *  이때는 빈칸 대신 날짜를 보여주고, 시간이 없다는 사실은 별도 라벨로 알린다.
 */
function formatTimeRange(row: DailyReservationRow): { label: string; noTime: boolean } {
  const isMultiDay =
    row.reservEndDate != null && row.reservStartDate !== row.reservEndDate;

  if (isMultiDay) {
    // 다일 예약 (호텔 등). 시간 미지정이면 날짜만 표기.
    const startPart = row.startTime
      ? `${row.reservStartDate} ${row.startTime}`
      : row.reservStartDate;
    const endPart = row.endTime
      ? `${row.reservEndDate} ${row.endTime}`
      : row.reservEndDate;
    return { label: `${startPart} ~ ${endPart}`, noTime: !row.startTime && !row.endTime };
  }

  if (!row.startTime) {
    return { label: row.reservStartDate ?? '-', noTime: true };
  }
  return {
    label: `${row.startTime}${row.endTime ? ` ~ ${row.endTime}` : ''}`,
    noTime: false,
  };
}

/** 매장별 집계 */
interface CompanyGroup {
  companySeq: number;
  companyName: string;
  rows: DailyReservationRow[];
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  canceled: number;
}

export function DailyReservationsPage() {
  const [date, setDate] = useState<string>(todayStr());
  const [keyword, setKeyword] = useState<string>('');
  const [appliedKeyword, setAppliedKeyword] = useState<string>('');
  const [reservType, setReservType] = useState<ReservType | 'ALL'>('ALL');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [tab, setTab] = useState<StatusTab>('ALL');

  const listRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // 검색어 디바운스 (300ms)
  useEffect(() => {
    const t = setTimeout(() => setAppliedKeyword(keyword.trim()), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['dailyReservations', { date, keyword: appliedKeyword, reservType }],
    queryFn: () =>
      dailyReservationsApi.getByDate({
        date,
        keyword: appliedKeyword || undefined,
        reservType,
        // 상태는 우측 패널 탭에서 나누므로 서버에서는 전체를 받는다
        status: 'ALL',
      }),
    staleTime: 30 * 1000,
  });

  /** 어드민 직권 취소 mutation */
  const cancelMutation = useMutation({
    mutationFn: (params: { reservationSeq: number; reason?: string }) =>
      dailyReservationsApi.cancelReservation(params),
    onSuccess: result => {
      // 예약 + 이용권 캐시 모두 갱신
      queryClient.invalidateQueries({ queryKey: ['dailyReservations'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketDetail'] });

      // cascade 환원 결과 안내
      if (result.cancelledTicketUses > 0) {
        alert(
          `예약이 취소되었습니다.\n` +
            `이용권 ${result.cancelledTicketUses}건 (총 ${result.restoredTicketQty}회/박) 이 함께 환원되었습니다.`,
        );
      }
    },
    onError: (err: unknown) => {
      alert(
        err instanceof Error ? err.message : '취소 처리 중 오류가 발생했습니다.',
      );
    },
  });

  /** 어드민 대리 승인 mutation */
  const approveMutation = useMutation({
    mutationFn: (reservationSeq: number) =>
      dailyReservationsApi.approveReservation(reservationSeq),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyReservations'] });
    },
    onError: (err: unknown) => {
      alert(
        err instanceof Error ? err.message : '승인 처리 중 오류가 발생했습니다.',
      );
    },
  });

  const handleApprove = (row: DailyReservationRow) => {
    if (!APPROVABLE_STATUSES.has(row.status)) return;
    const ok = window.confirm(
      `예약을 승인(확정)하시겠습니까?\n\n` +
        `사업장: ${row.companyName}\n` +
        `보호자: ${row.userName} / ${row.petName}\n` +
        `일시: ${row.reservStartDate} ${row.startTime}\n\n` +
        `※ 승인 시 보호자에게 예약 확정 알림(알림장·푸시·알림톡)이 발송됩니다.`,
    );
    if (!ok) return;
    approveMutation.mutate(row.reservationSeq);
  };

  const handleCancel = (row: DailyReservationRow) => {
    if (!CANCELABLE_STATUSES.has(row.status)) return;
    const willRestoreTicket = row.paymentType === 'PAYMENT_TYPE_TICKET';
    const ticketNotice = willRestoreTicket
      ? `\n※ 이 예약은 이용권 결제이므로 취소 시 사용된 이용권이 함께 환원됩니다.\n`
      : '';
    const reason = window.prompt(
      `예약을 취소하시겠습니까?\n\n` +
        `사업장: ${row.companyName}\n` +
        `보호자: ${row.userName} / ${row.petName}\n` +
        `일시: ${row.reservStartDate} ${row.startTime}\n` +
        ticketNotice +
        `\n취소 사유를 입력해주세요 (선택, Enter 시 기본값 사용)`,
      '',
    );
    if (reason === null) return; // 사용자가 Cancel 누름
    cancelMutation.mutate({
      reservationSeq: row.reservationSeq,
      reason: reason.trim() || undefined,
    });
  };

  const rawRows = useMemo(() => data ?? [], [data]);

  /** 매장별 그룹 + 전체 통계 */
  const { groups, stats } = useMemo(() => {
    const map = new Map<number, CompanyGroup>();
    let confirmed = 0;
    let canceled = 0;
    let pending = 0;
    let completed = 0;

    for (const row of rawRows) {
      let g = map.get(row.companySeq);
      if (!g) {
        g = {
          companySeq: row.companySeq,
          companyName: row.companyName,
          rows: [],
          total: 0,
          pending: 0,
          confirmed: 0,
          completed: 0,
          canceled: 0,
        };
        map.set(row.companySeq, g);
      }
      g.rows.push(row);
      g.total++;

      switch (row.status) {
        case 'RESERV_STATUS_CONFIRMED':
          confirmed++;
          g.confirmed++;
          break;
        case 'RESERV_STATUS_CANCELED':
          canceled++;
          g.canceled++;
          break;
        case 'RESERV_STATUS_COMPLETED':
          completed++;
          g.completed++;
          break;
        default:
          pending++;
          g.pending++;
          break;
      }
    }

    // 대기 건이 많은 매장 → 총 건수 → 이름 순 (처리할 게 남은 매장이 위로)
    const sorted = [...map.values()].sort((a, b) => {
      if (b.pending !== a.pending) return b.pending - a.pending;
      if (b.total !== a.total) return b.total - a.total;
      return (a.companyName ?? '').localeCompare(b.companyName ?? '');
    });

    return {
      groups: sorted,
      stats: { total: rawRows.length, confirmed, pending, completed, canceled },
    };
  }, [rawRows]);

  const selected = groups.find(g => g.companySeq === selectedSeq) ?? null;

  // 날짜/필터가 바뀌어 선택 매장이 목록에서 사라지면 선택 해제
  useEffect(() => {
    if (selectedSeq !== null && !groups.some(g => g.companySeq === selectedSeq)) {
      setSelectedSeq(null);
    }
  }, [groups, selectedSeq]);

  // 매장이 바뀌면 상태 탭을 전체로 되돌린다
  useEffect(() => {
    setTab('ALL');
  }, [selectedSeq]);

  // 조회 조건이 바뀌면 매장 목록 스크롤을 위로
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [date, appliedKeyword, reservType]);

  /** 선택 매장의 예약 — 상태 탭 필터 + 시간순 정렬 */
  const selectedRows = useMemo(() => {
    if (!selected) return [];
    return selected.rows
      .filter(r => matchesTab(r, tab))
      .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
  }, [selected, tab]);

  const tabCounts: Record<StatusTab, number> = {
    ALL: selected?.total ?? 0,
    PENDING: selected?.pending ?? 0,
    CONFIRMED: selected?.confirmed ?? 0,
    COMPLETED: selected?.completed ?? 0,
    CANCELED: selected?.canceled ?? 0,
  };

  /** 엑셀은 현재 조회 조건의 전체 매장 예약을 그대로 내린다 (선택 매장에 한정하지 않음) */
  const handleExcel = () => {
    if (rawRows.length === 0) return;
    exportToExcel<DailyReservationRow>(
      `예약현황_${date}`,
      `${date}`,
      [
        { header: '사업장', accessor: r => r.companyName },
        { header: '예약유형', accessor: r => RESERV_TYPE_LABEL[r.reservType] },
        { header: '시작일', accessor: r => r.reservStartDate },
        { header: '종료일', accessor: r => r.reservEndDate },
        { header: '시작시각', accessor: r => r.startTime ?? '-' },
        { header: '종료시각', accessor: r => r.endTime ?? '-' },
        { header: '상태', accessor: r => STATUS_LABEL[r.status] },
        { header: '보호자', accessor: r => r.userName },
        { header: '반려동물', accessor: r => r.petName },
        { header: '연락처', accessor: r => r.userPhone },
        { header: '이용권', accessor: r => formatTicket(r) },
        { header: '메모', accessor: r => r.memo ?? '' },
      ],
      rawRows,
    );
  };

  return (
    // 페이지 전체를 한 화면에 — 헤더(64) + main 패딩 고려해 calc
    <div className="flex h-[calc(100vh-120px)] flex-col">
      <header className="mb-3 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <CalendarDays className="text-gray-500" size={22} /> 일자별 예약 현황
          </h1>
          <p className="mt-0.5 text-xs text-gray-500">
            좌측에서 매장을 선택하면 해당 매장의 예약이 우측에 표시됩니다.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex gap-1.5 text-xs">
            <span className="rounded-md bg-gray-100 px-2.5 py-1.5 text-gray-600">
              총 <strong className="tabular-nums">{stats.total}</strong>
            </span>
            <span className="rounded-md bg-blue-50 px-2.5 py-1.5 text-blue-700">
              대기 <strong className="tabular-nums">{stats.pending}</strong>
            </span>
            <span className="rounded-md bg-green-50 px-2.5 py-1.5 text-green-700">
              확정 <strong className="tabular-nums">{stats.confirmed}</strong>
            </span>
            <span className="rounded-md bg-red-50 px-2.5 py-1.5 text-red-700">
              취소 <strong className="tabular-nums">{stats.canceled}</strong>
            </span>
          </div>
          <button
            type="button"
            disabled={rawRows.length === 0}
            onClick={handleExcel}
            title="현재 조회 조건의 전체 매장 예약을 내려받습니다"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={16} />
            엑셀
          </button>
        </div>
      </header>

      {/* 좌측 매장 리스트 + 우측 예약 목록 */}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        {/* ── 좌측: 매장 리스트 (5/12) ── */}
        <div className="col-span-12 flex min-h-0 flex-col lg:col-span-5">
          {/* 날짜 + 예약유형 */}
          <div className="mb-2 flex gap-2">
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <select
              value={reservType}
              onChange={e => setReservType(e.target.value as ReservType | 'ALL')}
              className="flex-1 rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="ALL">예약유형 전체</option>
              <option value="RESERV_TYPE_DAYCARE">데이케어</option>
              <option value="RESERV_TYPE_HOTEL">호텔</option>
              <option value="RESERV_TYPE_CLASS">수업</option>
              <option value="RESERV_TYPE_GROOMING">미용</option>
              <option value="RESERV_TYPE_TRAINING">훈련</option>
            </select>
          </div>

          {/* 검색 */}
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="사업장 / 보호자 / 반려동물명"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              매장 {groups.length.toLocaleString()}곳 · 예약 {stats.total.toLocaleString()}건
            </span>
            {isFetching && !isLoading && <span className="text-gray-400">불러오는 중…</span>}
          </div>

          {/* 매장 리스트 — 내부 스크롤 */}
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div ref={listRef} className="h-full divide-y divide-gray-100 overflow-y-auto">
              {isLoading && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">로딩 중…</div>
              )}
              {!isLoading && error && (
                <div className="px-4 py-8 text-center text-sm text-red-500">
                  예약 정보를 불러오지 못했습니다.
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="ml-2 underline hover:text-red-700"
                  >
                    다시 시도
                  </button>
                </div>
              )}
              {!isLoading && !error && groups.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  {date} 에는 예약이 없습니다.
                </div>
              )}
              {!isLoading &&
                !error &&
                groups.map(g => {
                  const isSelected = selectedSeq === g.companySeq;
                  return (
                    <div
                      key={g.companySeq}
                      onClick={() => setSelectedSeq(g.companySeq)}
                      // 컨테이너의 divide-gray-100 규칙(명시도 0,3,0)이 border-color 단축 속성으로
                      // 좌측 테두리 색까지 덮어써서, ! 프리픽스로 선택 표시를 강제한다.
                      className={`cursor-pointer px-3 py-2.5 transition-colors ${
                        isSelected
                          ? 'border-l-4 !border-l-primary-500 bg-primary-50 pl-2'
                          : 'border-l-4 !border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-gray-900">
                              {g.companyName ?? `매장 #${g.companySeq}`}
                            </span>
                            {g.pending > 0 && (
                              <StatusBadge tone="info">대기 {g.pending}</StatusBadge>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-500">
                            확정 {g.confirmed} · 완료 {g.completed} · 취소 {g.canceled} · SEQ{' '}
                            {g.companySeq}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-base font-bold tabular-nums text-gray-900">
                            {g.total}
                          </div>
                          <div className="text-[10px] text-gray-500">건</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* ── 우측: 선택 매장의 예약 (7/12) ── */}
        <div className="col-span-12 min-h-0 lg:col-span-7">
          {!selected ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
              <div className="text-center">
                <Store className="mx-auto mb-2 text-gray-300" size={40} />
                <div className="text-sm text-gray-500">
                  좌측에서 매장을 선택하면
                  <br />
                  해당 매장의 예약이 표시됩니다
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col overflow-hidden rounded-lg border-2 border-primary-200 bg-white">
              {/* 헤더 */}
              <div className="shrink-0 border-b border-gray-200 px-5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-bold text-gray-900">
                      {selected.companyName}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        SEQ {selected.companySeq}
                      </span>
                    </h2>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {date} · 총 {selected.total}건
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedSeq(null)}
                    className="shrink-0 text-sm text-gray-500 hover:text-gray-900"
                  >
                    닫기 ×
                  </button>
                </div>
              </div>

              {/* 상태 탭 */}
              <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-3">
                <div className="flex gap-1">
                  {STATUS_TABS.map(t => {
                    const active = tab === t.key;
                    const count = tabCounts[t.key];
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={`-mb-px shrink-0 border-b-2 px-2.5 py-2 text-sm font-medium transition-colors ${
                          active
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                      >
                        {t.label}
                        {count > 0 && (
                          <span
                            className={`ml-1 text-xs tabular-nums ${
                              active ? 'text-primary-500' : 'text-gray-400'
                            }`}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 예약 목록 — 내부 스크롤 */}
              <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-4">
                {selectedRows.length === 0 ? (
                  <EmptyState message="해당 상태의 예약이 없습니다." />
                ) : (
                  <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {selectedRows.map(row => {
                      const cancelable = CANCELABLE_STATUSES.has(row.status);
                      const isPendingThis =
                        cancelMutation.isPending &&
                        cancelMutation.variables?.reservationSeq === row.reservationSeq;
                      const approvable = APPROVABLE_STATUSES.has(row.status);
                      const isApprovingThis =
                        approveMutation.isPending &&
                        approveMutation.variables === row.reservationSeq;
                      const anyBusy = cancelMutation.isPending || approveMutation.isPending;
                      const time = formatTimeRange(row);
                      const ticket = formatTicket(row);
                      const meta = [
                        `${row.userName} / ${row.petName}`,
                        row.userPhone || null,
                        ticket !== '-' ? ticket : null,
                        row.memo || null,
                      ]
                        .filter(Boolean)
                        .join(' · ');

                      return (
                        <div key={row.reservationSeq} className="px-3 py-2">
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="flex min-w-0 items-baseline gap-2">
                              <span className="shrink-0 text-sm font-semibold text-gray-900">
                                {time.label}
                              </span>
                              {time.noTime && (
                                <span
                                  className="shrink-0 text-[10px] text-gray-400"
                                  title="이 예약 유형은 시작/종료 시각을 저장하지 않습니다"
                                >
                                  시간 미지정
                                </span>
                              )}
                              <StatusBadge tone={STATUS_TONE[row.status]}>
                                {STATUS_LABEL[row.status]}
                              </StatusBadge>
                              <span className="shrink-0 text-[11px] text-gray-500">
                                {RESERV_TYPE_LABEL[row.reservType]}
                              </span>
                            </div>
                            {approvable || cancelable ? (
                              <div className="flex shrink-0 items-center gap-1">
                                {approvable && (
                                  <button
                                    type="button"
                                    disabled={anyBusy}
                                    onClick={() => handleApprove(row)}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                                  >
                                    <CheckCircle2 size={11} />
                                    {isApprovingThis ? '승인 중…' : '승인'}
                                  </button>
                                )}
                                {cancelable && (
                                  <button
                                    type="button"
                                    disabled={anyBusy}
                                    onClick={() => handleCancel(row)}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-0.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                                  >
                                    <XCircle size={11} />
                                    {isPendingThis ? '취소 중…' : '취소'}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="shrink-0 text-[10px] text-gray-300">-</span>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
                            {meta}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
